import { Fragment, useEffect, useState } from 'react';

import { Backdrop } from '@shared/atoms/backdrop';
import type { RouteElement } from '@shared/router';
import * as history from '@shared/router/location';

import { ClubSelectorItem } from '@playbook/compositions/club-selector-item';

import { ApiError } from '@portal/pkg/api';
import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';
import { PortalNavigation } from '@portal/views/_shared/navigation';
import { Notice } from '@portal/views/_shared/notice';
import { PageLoader } from '@portal/views/_shared/page-loader';

import css from './styles.module.css';

export default function OrganizationsPage(): RouteElement {
	const { status, organization, availableOrganizations, selectOrganization } =
		useSession();

	const [choosing, setChoosing] = useState<Nullable<number>>(null);
	const [error, setError] = useState<Nullable<string>>(null);

	useEffect(() => {
		if (status === 'anonymous') {
			history.replace(routes.Login());
		}
	}, [status]);

	// Leaving happens here, on a choice, and nowhere else. Redirecting on
	// `organization` being set would make the page unreachable for anyone who
	// already has one — which is everybody who comes here to switch.
	async function choose(organizationId: number): Promise<void> {
		setChoosing(organizationId);
		setError(null);

		try {
			await selectOrganization(organizationId);

			history.push(routes.Keys());
		} catch (caught) {
			setError(
				caught instanceof ApiError
					? caught.message
					: 'Could not switch organization. Try again.'
			);
		} finally {
			setChoosing(null);
		}
	}

	return (
		<Fragment>
			<Backdrop />
			<PortalNavigation variant="sticky" />
			<main className={css.main}>
				<div className={css.column}>
					<div className={css.heading}>
						<h1>Choose an organization</h1>
						<p>
							Keys belong to one organization. You can switch later without
							signing in again.
						</p>
					</div>

					{error && (
						<Notice variant="error" title="Could not switch">
							{error}
						</Notice>
					)}

					{status === 'loading' ? (
						<PageLoader />
					) : (
						/* No Card around these: ClubSelectorItem is already a
						   button, and nesting one inside Card's would be
						   invalid HTML. */
						<ul className={css.list}>
							{availableOrganizations.map((available) => (
								<li key={available.id}>
									<ClubSelectorItem
										label={available.name}
										description={
											available.id === organization?.id
												? 'Currently selected'
												: undefined
										}
										onClick={
											choosing === null
												? () => void choose(available.id)
												: undefined
										}
									/>
								</li>
							))}
						</ul>
					)}
				</div>
			</main>
		</Fragment>
	);
}
